<template>
  <div class="coro-widget-header">
    <span class="headline6">
      <span class="subtitle1">{{ $t(`dashboard.widgets.${widgetType}.title`) }}</span>
    </span>
    <v-icon
      v-if="showSettingsIcon"
      size="24"
      icon="$settings"
      class="cursor-pointer"
      @click="onSettingsIconClicked()"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, type PropType } from "vue";
import { WidgetType } from "@/constants/dashboard";
import { trackGtmEvent } from "@/_helpers/analytics";
import { useRouter } from "vue-router";
import { RouteName } from "@/constants/routes";
import { GtmAction, GTMParams } from "@/constants/gtm.ts";
import startCase from "lodash/startCase";
import { WIDGET_MODULE_MAP } from "@/_helpers/dashboard.ts";
import { isAccessRestricted } from "@/_helpers/permissions.ts";
import { RolePermissionsScope } from "@/constants/roles.ts";

export default defineComponent({
  props: {
    widgetType: {
      type: String as PropType<WidgetType>,
      required: true,
    },
  },
  setup(props) {
    const router = useRouter();
    const showSettingsIcon = computed(() => {
      const module = WIDGET_MODULE_MAP[props.widgetType];
      if (module) {
        return !isAccessRestricted(RolePermissionsScope.PROTECTION, module);
      }
      return false;
    });

    const onSettingsIconClicked = () => {
      switch (props.widgetType) {
        case WidgetType.CLOUD_SECURITY:
          router.push({ name: RouteName.CLOUD_SECURITY_PAGE });
          break;
        case WidgetType.ENDPOINT_SECURITY:
          router.push({ name: RouteName.ENDPOINT_SECURITY_DEVICE_POSTURE_TAB });
          break;
        case WidgetType.EMAIL_SECURITY:
          router.push({ name: RouteName.EMAIL_SECURITY_PAGE });
          break;
        case WidgetType.USER_DATA_GOVERNANCE:
          router.push({ name: RouteName.USER_DATA_GOVERNANCE_PAGE });
          break;
        case WidgetType.ENDPOINT_DATA_GOVERNANCE:
          router.push({ name: RouteName.ENDPOINT_DATA_GOVERNANCE_PAGE });
          break;
        case WidgetType.EDR:
          router.push({ name: RouteName.EDR_PROCESSES_TAB });
          break;
        case WidgetType.SECURITY_AWARENESS_TRAINING:
          router.push({ name: RouteName.PHISHING_SIMULATION });
          break;
        default:
          console.error(`Unhandled widgetType: ${props.widgetType}`);
          return;
      }
      trackGtmEvent(
        GtmAction.WORKSPACE_VIEW_DASHBOARD_WIDGET,
        `${startCase(props.widgetType)} - ${GTMParams.SETTINGS_ICON}`
      );
    };

    return {
      showSettingsIcon,
      onSettingsIconClicked,
    };
  },
});
</script>

<style lang="scss" scoped>
.coro-widget-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: 100%;
}
</style>
